import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowRight, ExternalLink, BookOpen, Lock, Landmark } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { RIGHTS_TOPICS, TOPIC_SLUGS } from "../data/rightsTopics";

function ResourceCard({ r, gov }) {
  return (
    <a
      href={r.url}
      target="_blank"
      rel="noopener noreferrer"
      className={`group glass-card p-5 flex flex-col items-center text-center transition-all relative ${
        gov
          ? "border-[#D4AF37]/40 hover:border-[#D4AF37]"
          : "hover:border-[#7DA3D9]/60"
      }`}
      title={`Visit ${r.name} → ${r.url}`}
      data-testid={`resource-${r.domain}`}
    >
      {gov && (
        <div className="absolute -top-2 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded-sm bg-[#D4AF37] text-[#0A1128] text-[9px] font-bold uppercase tracking-[0.15em] whitespace-nowrap notranslate">
          Official Gov
        </div>
      )}
      <div
        className={`w-20 h-20 rounded-md bg-white p-3 border transition-all shadow-lg mb-3 ${
          gov
            ? "border-[#D4AF37]/40 group-hover:border-[#D4AF37]"
            : "border-white/10 group-hover:border-[#7DA3D9]"
        } group-hover:scale-105`}
      >
        <img
          src={`https://logo.clearbit.com/${r.domain}`}
          alt={`${r.name} logo`}
          className="w-full h-full object-contain"
          onError={(e) => {
            e.currentTarget.onerror = null;
            e.currentTarget.src = `https://www.google.com/s2/favicons?domain=${r.domain}&sz=128`;
          }}
        />
      </div>
      <div
        className={`font-display text-base group-hover:text-[#B8D0EE] transition-colors leading-tight ${
          gov ? "text-[#F5E6B3]" : "text-white"
        }`}
      >
        {r.name}
      </div>
      <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500 mt-2 inline-flex items-center gap-1">
        Visit <ExternalLink className="w-3 h-3" />
      </div>
    </a>
  );
}

export default function RightsTopicPage() {
  const { slug } = useParams();
  const { user } = useAuth();
  const topic = RIGHTS_TOPICS[slug];

  if (!topic) return <Navigate to="/rights-library" replace />;

  const govResources = topic.resources.filter((r) => r.isGovernment);
  const privateResources = topic.resources.filter((r) => !r.isGovernment);

  return (
    <div className="min-h-screen bg-[#0A1128] pt-28 pb-20" data-testid={`rights-topic-${slug}`}>
      <div className="max-w-7xl mx-auto px-6">
        {/* Breadcrumb */}
        <nav className="text-xs text-slate-500 flex items-center gap-2 mb-6">
          <Link to="/" className="hover:text-white transition-colors">Home</Link>
          <span>/</span>
          <Link to="/rights-library" className="hover:text-white transition-colors">Rights Library</Link>
          <span>/</span>
          <span className="text-[#B8D0EE]">{topic.title}</span>
        </nav>

        {/* Hero */}
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-14">
          <div className="lg:col-span-8">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-[#7DA3D9]/25 bg-[#7DA3D9]/5 mb-6">
              <BookOpen className="w-3.5 h-3.5 text-[#7DA3D9]" />
              <span className="text-[10px] uppercase tracking-[0.25em] text-[#B8D0EE] font-semibold">
                {topic.overline}
              </span>
            </div>
            <h1 className="font-display text-4xl lg:text-7xl tracking-tight leading-[0.98]" data-testid="topic-title">
              {topic.title}
            </h1>
          </div>
          <div className="lg:col-span-4">
            <p className="text-slate-300 leading-relaxed text-lg">{topic.intro}</p>
          </div>
        </div>

        {/* Key points */}
        {topic.points?.length > 0 && (
          <div className="grid lg:grid-cols-3 gap-px bg-white/5 rounded-md overflow-hidden border border-white/5 mb-16">
            {topic.points.map((p, i) => (
              <div key={i} className="p-7 bg-[#0A1128]">
                <div className="text-[#7DA3D9] font-display text-2xl mb-3">{String(i + 1).padStart(2, "0")}</div>
                <p className="text-slate-300 text-sm leading-relaxed">{p}</p>
              </div>
            ))}
          </div>
        )}

        {/* ============= GOVERNMENT AGENCIES (clearly separated) ============= */}
        {govResources.length > 0 && (
          <section className="mb-16" data-testid="gov-agencies-section">
            <div className="flex items-center gap-3 mb-2">
              <Landmark className="w-5 h-5 text-[#D4AF37]" />
              <div className="overline" style={{ color: "#D4AF37" }}>
                Official Government Agencies
              </div>
            </div>
            <h2 className="font-display text-2xl lg:text-4xl mb-3">
              Government & intergovernmental authorities
            </h2>
            <p className="text-slate-400 text-sm mb-8 max-w-3xl">
              These are <span className="text-[#F5E6B3] font-semibold">official government bodies</span> —
              copyright offices, IP authorities, and statutory royalty boards. They administer
              registrations, set rates, and adjudicate disputes. Always start here when questions
              of law arise.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 pt-3" data-testid="gov-grid">
              {govResources.map((r) => (
                <ResourceCard key={r.url} r={r} gov />
              ))}
            </div>
          </section>
        )}

        {/* ============= PRIVATE / TRADE / ADVOCACY ============= */}
        {privateResources.length > 0 && (
          <div className="mb-10" data-testid="private-resources-section">
            <div className="overline mb-3">
              {govResources.length > 0 ? "Private agencies, societies & companies" : "Key agencies & resources"}
            </div>
            <h2 className="font-display text-2xl lg:text-4xl mb-2">
              Click any logo to visit that organization
            </h2>
            <p className="text-slate-400 text-sm mb-8">
              {govResources.length > 0
                ? "Trade associations, performing rights organizations, publishers, advocacy groups, clearance houses, and licensing companies — all non-government entities active in this rights area."
                : `Every icon below links directly to the official site of that agency, publisher, collective, or authority. Hand-curated for ${topic.title.toLowerCase()}.`}
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="resource-grid">
              {privateResources.map((r) => (
                <ResourceCard key={r.url} r={r} />
              ))}
            </div>
          </div>
        )}

        {/* Membership CTA / paywall hint */}
        {!user?.has_complete_access && (
          <div className="glass-card p-8 lg:p-10 mt-12 border-[#D4AF37]/30" data-testid="topic-paywall-cta">
            <div className="flex items-start gap-4">
              <Lock className="w-6 h-6 text-[#D4AF37] flex-shrink-0 mt-1" />
              <div>
                <div className="overline" style={{ color: "#D4AF37" }}>Limited time · only $6.95</div>
                <h3 className="font-display text-2xl lg:text-3xl mt-2 mb-3">
                  Unlock the full 200+ agency directory
                </h3>
                <p className="text-slate-300 mb-5 max-w-2xl">
                  For a limited time, join OSRR for just <span className="text-[#D4AF37] font-semibold">$6.95</span> —
                  lifetime access to every PRO, MRO, publisher, sync library, and master-rights collective indexed across 160 countries.
                </p>
                <Link to="/upgrade" className="btn-gold inline-flex items-center gap-2">
                  Unlock for $6.95 <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        )}

        {/* Related topics */}
        <section className="mt-20" data-testid="related-topics">
          <div className="overline mb-3">Continue exploring</div>
          <h2 className="font-display text-2xl lg:text-3xl mb-6">Other rights topics</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {TOPIC_SLUGS.filter((s) => s !== slug)
              .slice(0, 8)
              .map((s) => {
                const t = RIGHTS_TOPICS[s];
                return (
                  <Link
                    key={s}
                    to={`/rights-topic/${s}`}
                    className="glass-card p-5 group"
                    data-testid={`related-topic-${s}`}
                  >
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[#7DA3D9]">
                      {t.overline}
                    </div>
                    <div className="font-display text-lg mt-2 group-hover:text-[#B8D0EE] transition-colors">
                      {t.title}
                    </div>
                  </Link>
                );
              })}
          </div>
          <div className="mt-6">
            <Link to="/rights-library" className="text-[#7DA3D9] hover:text-[#B8D0EE] inline-flex items-center gap-1.5 text-sm">
              View all {TOPIC_SLUGS.length} rights topics <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
}
